import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';

/**
 * Install a serialized component at a given installation point.
 *
 * The picker is filtered to `new` + `available` parts. If the target point
 * is already occupied, the backend refuses with 400 — surface that plainly.
 * `installed_at` defaults to now but is editable so techs can log a swap
 * that happened earlier in the day.
 */
const InstallComponentDialog = ({ open, onClose, onSuccess, installationPoint, equipmentName }) => {
  const [components, setComponents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [componentId, setComponentId] = useState('');
  const [installedAt, setInstalledAt] = useState(() => {
    const d = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  });
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!open) return;
    setComponentId('');
    setNotes('');
    setSearch('');
    setLoading(true);
    (async () => {
      try {
        const machineryId = installationPoint?.machinery_id;
        // Filter by both status AND compatibility with the target machinery
        // so techs never see a wrong-fit part in the picker.
        const params = machineryId ? `&applicable_to=${encodeURIComponent(machineryId)}` : '';
        const [newRes, availRes] = await Promise.all([
          axios.get(`${API}/serialized-components?status=new${params}`),
          axios.get(`${API}/serialized-components?status=available${params}`),
        ]);
        setComponents([...(newRes.data || []), ...(availRes.data || [])]);
      } catch (err) {
        toast.error('Failed to load serialized components');
      } finally {
        setLoading(false);
      }
    })();
  }, [open, installationPoint]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return components;
    return components.filter((c) =>
      (c.serial_number || '').toLowerCase().includes(q) ||
      (c.name || '').toLowerCase().includes(q) ||
      (c.mfr_part_number || '').toLowerCase().includes(q) ||
      (c.manufacturer || '').toLowerCase().includes(q) ||
      (c.model || '').toLowerCase().includes(q)
    );
  }, [components, search]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!componentId) {
      toast.error('Pick a component to install');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        installation_point_id: installationPoint.id,
        installed_at: installedAt ? new Date(installedAt).toISOString() : null,
        notes: notes.trim() || null,
      };
      await axios.post(`${API}/serialized-components/${componentId}/install`, payload);
      toast.success(`Installed at ${installationPoint.label}`);
      onSuccess && onSuccess();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Install failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (!installationPoint) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="install-component-dialog">
        <DialogHeader>
          <DialogTitle>Install Part</DialogTitle>
          <DialogDescription>
            {equipmentName} · <span className="font-medium">{installationPoint.label}</span>
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="ic-search">Find serialized part</Label>
            <Input
              id="ic-search"
              data-testid="ic-search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search MFR part #, serial, name, manufacturer…"
            />
            <p className="text-xs text-slate-500">
              Only parts marked compatible with this equipment (or applies-to-any) are shown.
            </p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ic-component">Component *</Label>
            <select
              id="ic-component"
              data-testid="ic-component-select"
              value={componentId}
              onChange={(e) => setComponentId(e.target.value)}
              className="flex h-9 w-full items-center justify-between rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm ring-offset-background focus:outline-none focus:ring-1 focus:ring-ring"
              required
            >
              <option value="">
                {loading
                  ? 'Loading…'
                  : filtered.length === 0
                    ? (components.length === 0
                        ? 'No compatible parts marked New or Available'
                        : 'No matches — refine search')
                    : 'Choose a part…'}
              </option>
              {filtered.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.mfr_part_number ? `[${c.mfr_part_number}] ` : ''}
                  SN {c.serial_number} — {c.name}
                  {c.status === 'new' ? ' (New)' : ' (Available)'}
                </option>
              ))}
            </select>
            <p className="text-xs text-slate-500">
              Only parts with status New or Available can be installed. To install
              a rebuilt part, flip it to Available from its detail page first.
            </p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ic-installed-at">Install time</Label>
            <Input
              id="ic-installed-at"
              data-testid="ic-installed-at-input"
              type="datetime-local"
              value={installedAt}
              onChange={(e) => setInstalledAt(e.target.value)}
            />
            <p className="text-xs text-slate-500">
              This starts the &ldquo;time in service&rdquo; clock for this component.
              Defaults to now; back-date if the swap happened earlier in the day.
            </p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ic-notes">Notes</Label>
            <Textarea
              id="ic-notes"
              data-testid="ic-notes-input"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="Optional — e.g. rebuilt torque values, break-in checks"
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={submitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting || !componentId} data-testid="ic-submit-btn">
              {submitting ? 'Installing…' : 'Install'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default InstallComponentDialog;
